/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  ChargingScheduleProperties,
  ChargingScheduleRequest,
  ChargingScheduleResponse,
  ChargingScheduleInitialProperties,
} from '../../interface/charging-schedule.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import device from '../../utils/api/device';

// a thunk to fetch users
export const fetchCharingSchedules = createAsyncThunk<
  ChargingScheduleResponse,
  ChargingScheduleRequest
>(
  'charging-schedule/fetchCharingSchedules',
  async (data: ChargingScheduleRequest, { dispatch }) => {
    try {
      const response = await device.fetchChargingSchedules(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// a thunk to refresh charging schedules
export const refreshChargingSchedules = createAsyncThunk<
  ChargingScheduleResponse,
  ChargingScheduleRequest
>(
  'charging-schedule/refreshChargingSchedules',
  async (data: ChargingScheduleRequest) => {
    const response = await device.fetchChargingSchedules(data);
    return response.data;
  },
);


// a thunk to fetch devices filters
export const fetchChargingScheduleFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('charging-schedule/fetchChargingScheduleFilters', async (_, { dispatch }) => {
  try {
    const response = await device.fetchChargingScheduleFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ChargingScheduleInitialProperties = {
  schedules: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: deviceFilter,
  filterListLoading: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const chargingScheduleSlice = createSlice({
  name: 'charging-schedule',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateChargingScheduleFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
      }
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchCharingSchedules.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCharingSchedules.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload?.results[0]);
        state.schedules = action.payload?.results?.length
          ? action.payload.results?.map((eachSchedule: ChargingScheduleProperties) => ({
              ...eachSchedule,
              id: eachSchedule._id,
              bookingFrom: `${ConvertUTCtoDate(eachSchedule.startDateUtc || '')} - ${FormatUtcString(ConvertUTCtoHHMM(eachSchedule.startDateUtc || ''))}`,
              bookingTo: `${ConvertUTCtoDate(eachSchedule.endDateUtc || '')} - ${FormatUtcString(ConvertUTCtoHHMM(eachSchedule.endDateUtc || ''))}`,
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchCharingSchedules.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshChargingSchedules.fulfilled, (state, action) => {
      state.schedules = action.payload?.results?.length
        ? action.payload.results?.map((eachSchedule: ChargingScheduleProperties) => ({
            ...eachSchedule,
            id: eachSchedule._id,
            bookingFrom: `${ConvertUTCtoDate(eachSchedule.startDateUtc || '')} - ${FormatUtcString(ConvertUTCtoHHMM(eachSchedule.startDateUtc || ''))}`,
            bookingTo: `${ConvertUTCtoDate(eachSchedule.endDateUtc || '')} - ${FormatUtcString(ConvertUTCtoHHMM(eachSchedule.endDateUtc || ''))}`,
          }))
        : [];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchChargingScheduleFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchChargingScheduleFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchChargingScheduleFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateChargingScheduleFilters, setSortData } = chargingScheduleSlice.actions;
// Export the reducer
export default chargingScheduleSlice.reducer;
